import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Gavel, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/use-auth";

const authSearchSchema = z.object({
  mode: z.enum(["signin", "signup"]).optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/auth")({
  validateSearch: (s) => authSearchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Sign in — BidNova Auctions" },
      { name: "description", content: "Sign in or create an account to bid on graded Pokémon cards in GBP on BidNova Auctions." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Sign in — BidNova Auctions" },
      { property: "og:description", content: "Sign in to BidNova Auctions." },
      { property: "og:url", content: "https://bidnovaauctions.uk/auth" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/auth" }],
  }),
  component: AuthPage,
});

const signupSchema = z.object({
  displayName: z.string().trim().min(2, "Please enter your name").max(60),
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
  acceptTerms: z.literal("on", { errorMap: () => ({ message: "You must accept the Terms and Privacy Policy to create an account" }) }),
});
const signinSchema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});

function AuthPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState<"signin" | "signup">(search.mode ?? "signin");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      navigate({ to: search.redirect ?? "/dashboard", replace: true });
    }
  }, [authLoading, user, navigate, search.redirect]);

  const handleGoogle = async () => {
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      navigate({ to: search.redirect ?? "/dashboard", replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Google sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    const emailInput = document.getElementById("signin-email") as HTMLInputElement | null;
    const email = emailInput?.value?.trim() ?? "";
    const parsed = z.string().email().safeParse(email);
    if (!parsed.success) {
      toast.error("Enter your email above, then click Forgot password again");
      emailInput?.focus();
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Password reset email sent — check your inbox");
  };


  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const parsed = signinSchema.safeParse({
      email: form.get("email"),
      password: form.get("password"),
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword(parsed.data);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Welcome back");
    navigate({ to: search.redirect ?? "/dashboard", replace: true });
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const parsed = signupSchema.safeParse({
      displayName: form.get("displayName"),
      email: form.get("email"),
      password: form.get("password"),
      acceptTerms: form.get("acceptTerms"),
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: parsed.data.displayName },
      },
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Account created — you're signed in");
    navigate({ to: search.redirect ?? "/dashboard", replace: true });
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, oklch(0.72 0.14 82 / 25%), transparent 70%)" }} />
      <div className="relative w-full max-w-md">
        <div className="mb-6 text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-2">
            <Gavel className="h-5 w-5 text-gold" aria-hidden="true" />
            <span className="font-display text-xl">
              <span className="text-gold-gradient font-semibold">BidNova</span> Auctions
            </span>
          </Link>
          <h1 className="mt-4 font-display text-2xl text-foreground">
            {tab === "signup" ? "Create your account" : "Sign in to your account"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {tab === "signup"
              ? "Bid on graded cards and list your own in minutes."
              : "Welcome back — pick up where you left off."}
          </p>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur p-8 shadow-elegant gold-ring">
          <Tabs value={tab} onValueChange={(v) => setTab(v as "signin" | "signup")}>
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-surface-2">
              <TabsTrigger value="signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup">Create account</TabsTrigger>
            </TabsList>

            <Button
              type="button"
              variant="outline"
              className="w-full min-h-11 border-border/60 bg-surface hover:bg-surface-2"
              onClick={handleGoogle}
              disabled={busy}
            >
              <GoogleIcon className="mr-2 h-4 w-4" />
              Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border/60" />
              <span>or</span>
              <div className="h-px flex-1 bg-border/60" />
            </div>

            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input id="signin-email" name="email" type="email" autoComplete="email" required />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password">Password</Label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={busy}
                      className="rounded px-1 py-1 text-xs text-gold hover:underline disabled:opacity-50"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input id="signin-password" name="password" type="password" autoComplete="current-password" required />
                </div>
                <Button type="submit" disabled={busy} className="w-full min-h-11 bg-gold-gradient hover:opacity-90">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Display name</Label>
                  <Input id="signup-name" name="displayName" type="text" autoComplete="name" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input id="signup-email" name="email" type="email" autoComplete="email" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input id="signup-password" name="password" type="password" autoComplete="new-password" required minLength={8} />
                  <p className="text-xs text-muted-foreground">At least 8 characters.</p>
                </div>
                <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    required
                    className="mt-0.5 h-4 w-4 rounded border-border/60 bg-surface accent-[hsl(var(--gold))]"
                  />
                  <span>
                    I confirm I am at least 18 and I accept the{" "}
                    <Link to="/legal/terms" target="_blank" className="text-gold hover:underline">Terms &amp; Conditions</Link>
                    {" "}and{" "}
                    <Link to="/legal/privacy" target="_blank" className="text-gold hover:underline">Privacy Policy</Link>.
                  </span>
                </label>
                <Button type="submit" disabled={busy} className="w-full min-h-11 bg-gold-gradient hover:opacity-90">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 48 48" {...props}>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.3 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.1 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.2 35.6 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
